"use client";

import Image from "next/image";
import Link from "next/link";
import { Phone } from "lucide-react";
import { getFooterData } from "@/data/footer";

interface SharedDict {
  footer?: {
    copyright?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
}

interface FooterProps {
  lang: "en" | "jp";
}

const Footer = ({ lang }: FooterProps) => {
  const data = getFooterData(lang);

  // Helper function to handle mixed link formats (string or FooterLink object)
  const renderSectionLinks = (
    sectionTitle: string,
    baseHref?: string,
    params?: "queryParams" | "pathParams",
    slice?: [number, number]
  ) => {
    const section = data.sections.find((s) => s.title === sectionTitle);
    const links = slice
      ? section?.links.slice(slice[0], slice[1])
      : section?.links;

    return links?.map((link, index) => {
      const linkText = typeof link === "string" ? link : link.label;
      let linkHref;

      if (typeof link === "string" && baseHref) {
        // For special cases with baseHref (like News, Services, Projects, Sustainability)
        linkHref =
          params === "queryParams"
            ? `/${lang}${baseHref}=${encodeURIComponent(link)}`
            : `/${lang}${baseHref}/${encodeURIComponent(link)}`;
      } else if (typeof link === "string") {
        // For string links without baseHref, use parent section's href as prefix
        linkHref = `/${lang}${section?.href}/${encodeURIComponent(link)}`;
      } else {
        // For FooterLink objects, check if href already includes the parent route
        const linkObj = link as { label: string; href: string };
        if (linkObj.href.startsWith(`/${sectionTitle.toLowerCase()}`)) {
          // If it already has parent prefix, just add language
          linkHref = `/${lang}${linkObj.href}`;
        } else if (linkObj.href.startsWith("/")) {
          // If it's an absolute path starting with /, add language prefix
          linkHref = `/${lang}${linkObj.href}`;
        } else if (linkObj.href.startsWith("#")) {
          // If it's a hash-only href, concatenate directly to avoid adding an extra slash
          linkHref = `/${lang}${section?.href}${linkObj.href}`;
        } else {
          // If it's a relative path, add parent section's href as prefix
          linkHref = `/${lang}${section?.href}/${linkObj.href}`;
        }
      }

      return (
        <li key={index}>
          <Link
            href={linkHref}
            className="text-gray-50 hover:text-base-white transition-colors"
            onClick={(e) => {
              // Handle smooth scroll for hash links
              if (linkHref.includes("#")) {
                e.preventDefault();
                const targetId = linkHref.split("#")[1];
                const targetElement = document.getElementById(targetId);

                if (targetElement) {
                  // If we're on the same page, scroll smoothly
                  const currentPath = window.location.pathname;
                  const linkPath = linkHref.split("#")[0];
                  if (currentPath === linkPath) {
                    targetElement.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  } else {
                    // If we're navigating to a different page, let Next.js handle it
                    window.location.href = linkHref;
                  }
                } else {
                  // Fallback to normal navigation
                  window.location.href = linkHref;
                }
              }
            }}
          >
            <span className="text-sm font-medium">{linkText}</span>
          </Link>
        </li>
      );
    });
  };

  // Helper function for horizontal links (like in bottom bar)
  const renderHorizontalLinks = (sectionTitle: string) => {
    const section = data.sections.find((s) => s.title === sectionTitle);
    return section?.links.map((link, index) => {
      const linkHref = `/${lang}${link.href}`;
      return (
        <Link
          key={index}
          href={linkHref}
          className="text-sm text-gray-50 hover:text-base-white transition-colors whitespace-nowrap"
          onClick={(e) => {
            // Handle smooth scroll for hash links
            if (linkHref.includes("#")) {
              e.preventDefault();
              const targetId = linkHref.split("#")[1];
              const targetElement = document.getElementById(targetId);

              if (targetElement) {
                // If we're on the same page, scroll smoothly
                const currentPath = window.location.pathname;
                const linkPath = linkHref.split("#")[0];
                if (currentPath === linkPath) {
                  targetElement.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                } else {
                  // If we're navigating to a different page, let Next.js handle it
                  window.location.href = linkHref;
                }
              } else {
                // Fallback to normal navigation
                window.location.href = linkHref;
              }
            }
          }}
        >
          {link.label}
        </Link>
      );
    });
  };

  // Helper function to render section headers
  const renderSectionHeader = (sectionTitle: string) => {
    const section = data.sections.find((s) => s.title === sectionTitle);
    if (!section) return null;

    const linkHref = `/${lang}${section.href}`;
    return (
      <Link
        href={linkHref}
        onClick={(e) => {
          // Handle smooth scroll for hash links
          if (linkHref.includes("#")) {
            e.preventDefault();
            const targetId = linkHref.split("#")[1];
            const targetElement = document.getElementById(targetId);

            if (targetElement) {
              // If we're on the same page, scroll smoothly
              const currentPath = window.location.pathname;
              const linkPath = linkHref.split("#")[0];
              if (currentPath === linkPath) {
                targetElement.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              } else {
                // If we're navigating to a different page, let Next.js handle it
                window.location.href = linkHref;
              }
            } else {
              // Fallback to normal navigation
              window.location.href = linkHref;
            }
          }
        }}
      >
        <h3 className="text-xl font-bold text-base-white mb-6 hover:text-gray-200 transition-colors cursor-pointer">
          {section.displayName}
        </h3>
      </Link>
    );
  };

  return (
    <footer className="relative">
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src={data.backgroundImage.src}
          alt={data.backgroundImage.alt}
          fill
          className="object-cover"
          priority={false}
          draggable={false}
        />
        <div className="absolute inset-0 bg-[#2D3748]/95"></div>
      </div>{" "}
      {/* Content */}
      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="px-4 sm:px-6 lg:px-[62px]">
          <div className="mx-auto py-[60px] max-w-[1317px]">
            {/* Top Section - 6 columns with Services taking double space */}
            <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-12 mb-20">
              {/* About Section */}
              <div className="space-y-6 lg:col-span-1">
                {renderSectionHeader("About")}
                <ul className="space-y-4">{renderSectionLinks("About")}</ul>
              </div>

              {/* News Section */}
              <div className="space-y-6 lg:col-span-1 lg:-ml-[30px] xl:ml-0">
                {renderSectionHeader("News")}
                <ul className="space-y-4">
                  {renderSectionLinks(
                    "News",
                    "/news?category",
                    "queryParams",
                    [1, 4]
                  )}
                </ul>
              </div>

              {/* Services Section - Takes double space */}
              <div className="space-y-6 lg:col-span-2 lg:-ml-[65px] xl:ml-0">
                {renderSectionHeader("Services")}
                <ul className="space-y-4">
                  {renderSectionLinks("Services", "/services", "pathParams")}
                </ul>
              </div>

              {/* Projects Section */}
              <div className="space-y-6 lg:-ml-[80px]">
                {renderSectionHeader("Projects")}
                <ul className="space-y-4">
                  {renderSectionLinks(
                    "Projects",
                    "/projects?type",
                    "queryParams"
                  )}
                </ul>
              </div>

              {/* Our Office Section */}
              <div className="space-y-6 hidden lg:block lg:-ml-[80px]">
                <h3 className="text-xl font-bold text-base-white mb-6">
                  Our Office
                </h3>
                <div className="space-y-6">
                  <p className="text-sm text-gray-50 leading-relaxed">
                    {data.contactInfo.address}
                  </p>
                  <div className="space-y-3">
                    {data.contactInfo.phones.map((phone, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Phone size={16} className="text-gray-50" />
                        <span className="text-sm text-gray-50">{phone}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Section - 5 columns layout with proper alignment */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
              {/* Sustainability Section */}
              <div className="space-y-6">
                {renderSectionHeader("Sustainability")}
                <ul className="space-y-4">
                  {renderSectionLinks(
                    "Sustainability",
                    "/sustainability",
                    "pathParams"
                  )}
                </ul>
              </div>

              {/* Contact Section */}
              <div className="space-y-6">
                {renderSectionHeader("Contact")}
                <ul className="space-y-4">{renderSectionLinks("Contact")}</ul>
              </div>

              {/* Career Section */}
              <div className="space-y-6">
                {renderSectionHeader("Career")}
                <ul className="space-y-4">{renderSectionLinks("Career")}</ul>
              </div>

              {/* Our Office Section */}
              <div className="space-y-6 block lg:hidden">
                <h3 className="text-xl font-bold text-base-white mb-6">
                  Our Office
                </h3>
                <div className="space-y-6">
                  <p className="text-sm text-gray-50 leading-relaxed">
                    {data.contactInfo.address}
                  </p>
                  <div className="space-y-3">
                    {data.contactInfo.phones.map((phone, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Phone size={16} className="text-gray-50" />
                        <span className="text-sm text-gray-50">{phone}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Empty column for offset */}
              <div className="hidden md:block lg:col-span-2"></div>

              {/* Company Logo with Link */}
              <div className="w-[100px] h-[37px] lg:-ml-[80px]">
                <Link
                  href={data.companyLogo.href}
                  target="_blank"
                  className="block w-full h-full transition-opacity"
                >
                  <Image
                    src={data.companyLogo.src}
                    alt={data.companyLogo.alt}
                    width={data.companyLogo.width}
                    height={data.companyLogo.height}
                    className="w-full h-full object-contain filter brightness-0 invert opacity-90"
                    draggable={false}
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 bg-[#293239]">
          <div className="container mx-auto py-[30px] px-4 2xl:px-0">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex flex-col-reverse md:flex-row items-center gap-6 md:gap-8">
                <p className="text-sm text-gray-50 font-medium text-center md:text-left">
                  {data.copyright}
                </p>

                {/* Legal Links */}
                <div className="flex items-center gap-4">
                  {renderHorizontalLinks("Legal")}
                </div>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-3">
                {data.socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    className={`w-10 h-10 rounded-[20px] border border-[#3D434C] flex items-center justify-center text-gray-50 transition-all duration-300 group relative overflow-hidden hover:scale-110 hover:border-opacity-50`}
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src={social.icon}
                      alt={`${social.label} icon`}
                      width={16}
                      height={16}
                      className="group-hover:opacity-0 transition-all duration-300 ease-in-out transform group-hover:scale-75"
                    />
                    {social.hoverIcon && (
                      <Image
                        src={social.hoverIcon}
                        alt={`${social.label} hover icon`}
                        width={16}
                        height={16}
                        className="absolute inset-0 m-auto opacity-0 group-hover:opacity-100 transition-all duration-300 ease-in-out transform scale-75 group-hover:scale-100"
                      />
                    )}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
